import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, LogIn } from 'lucide-react';
import MainLayout from '../layout/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { GUIDE_CONTENT, GuideRole } from './guideContent';
import './guide.scss';

const GuidePage: React.FC = () => {
  const { role } = useParams<{ role: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const key = (role || '') as GuideRole;
  const c = GUIDE_CONTENT[key];

  if (!c) return <Navigate to="/" replace />;

  const standalone = !user;               // visible sin login (desde el correo de invitación)
  const other: GuideRole = c.role === 'mentee' ? 'mentor' : 'mentee';

  const body = (
    <div className="guide">
      {!standalone && (
        <button className="guide-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Volver
        </button>
      )}

      {/* Hero */}
      <header className="guide-hero">
        <span className="guide-badge">{c.badge}</span>
        <h1 className="guide-title">
          {c.titleLead} <em>{c.titleAccent}</em>
        </h1>
        <p className="guide-lead">{c.lead}</p>
      </header>

      {/* Recorrido */}
      <section className="guide-journey">
        <div className="guide-journey-label">El recorrido</div>
        <ol className="guide-journey-track">
          {c.journey.map((j, i) => (
            <li className="guide-journey-step" key={i}>
              <span className="gjs-dot">{j.icon}</span>
              <span className="gjs-label">{j.label}</span>
              {i < c.journey.length - 1 && <ChevronRight className="gjs-arrow" size={16} />}
            </li>
          ))}
        </ol>
      </section>

      {/* Pasos */}
      <section className="guide-steps">
        {c.steps.map((s, i) => (
          <article className="guide-step" key={i}>
            <div className="gst-index">{String(i + 1).padStart(2, '0')}</div>
            <div className="gst-icon">{s.icon}</div>
            <div className="gst-body">
              <h3 className="gst-title">{s.title}</h3>
              <p className="gst-text">{s.text}</p>
              {s.tip && (
                <p className="gst-tip"><span className="gst-tip-label">Consejo</span> {s.tip}</p>
              )}
            </div>
          </article>
        ))}
      </section>

      {/* Herramientas */}
      <section className="guide-features">
        <div className="guide-features-head">
          <span className="guide-features-eyebrow">Todo a la mano</span>
          <h2 className="guide-features-title">Tus herramientas en la plataforma</h2>
        </div>
        <div className="guide-features-grid">
          {c.features.map((f, i) => (
            <div className="guide-feature" key={i}>
              <span className="gf-icon">{f.icon}</span>
              <h4 className="gf-title">{f.title}</h4>
              <p className="gf-text">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cierre */}
      <footer className="guide-cta">
        <div className="guide-cta-main">
          <h3>¿Listo para empezar?</h3>
          <p>{standalone ? 'Ingresa con los datos de acceso que recibiste por correo.' : 'Entra a tu plan y avanza al siguiente paso de tu programa.'}</p>
          <button className="guide-cta-btn" onClick={() => navigate(standalone ? '/login' : `/${c.role}/plan`)}>
            {standalone ? 'Logearse al sistema' : 'Ir a mi plan'} <ArrowRight size={16} />
          </button>
        </div>
        <button className="guide-cta-other" onClick={() => navigate(`/guia/${other}`)}>
          Ver la guía del {other} <ChevronRight size={15} />
        </button>
      </footer>
    </div>
  );

  if (standalone) {
    return (
      <div className="guide-shell">
        <header className="guide-shell-top">
          <img src="/img/mitsui_logo_corporate.svg" alt="MITSUI & CO." className="guide-shell-logo" />
          <button className="guide-shell-login" onClick={() => navigate('/login')}>
            <LogIn size={15} /> Ingresar
          </button>
        </header>
        <main>{body}</main>
        <footer className="guide-shell-foot">
          <span>© {new Date().getFullYear()} Mitsui &amp; Co., Ltd.</span>
          <span className="guide-shell-foot-kuali">Desarrollado por Kuali</span>
        </footer>
      </div>
    );
  }

  return <MainLayout>{body}</MainLayout>;
};

export default GuidePage;
